import type { ProductUI } from "@/shared/types";

export type WholesaleLabel = "мелкий опт" | "крупный опт";

export interface ResolvedProductPrice {
  /** Цена за единицу продажи (шт) */
  unitPrice: number;
  /** Базовая/оптовая цена за кг (если задан средний вес штуки) */
  pricePerKg?: number;
  wholesaleLabel: WholesaleLabel | null;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Есть средний вес штуки (поле WEIGHT_OF_THE_FISH) — цена за кг + за шт */
export function isFishProduct(product: Pick<ProductUI, "weightOfTheFish">): boolean {
  return (product.weightOfTheFish ?? 0) > 0;
}

/** Количество для сравнения с DISC_COUNT: при весе штуки — в кг, иначе — в штуках */
export function getDiscountQuantity(
  product: Pick<ProductUI, "weightOfTheFish">,
  quantity: number
): number {
  if (isFishProduct(product)) {
    return quantity * (product.weightOfTheFish ?? 0);
  }
  return quantity;
}

export function resolveProductPrice(
  product: ProductUI,
  quantity: number
): ResolvedProductPrice {
  const safeQty = Math.max(quantity, 0);
  const discQty = getDiscountQuantity(product, safeQty);

  let effectivePrice = product.price;
  let wholesaleLabel: WholesaleLabel | null = null;

  const discCount2 = product.discCount2 ?? 0;
  const discCount1 = product.discCount1 ?? 0;

  if (discCount2 > 0 && discQty > discCount2) {
    effectivePrice = product.price2 ?? product.price;
    wholesaleLabel = "крупный опт";
  } else if (discCount1 > 0 && discQty > discCount1) {
    effectivePrice = product.price1 ?? product.price;
    wholesaleLabel = "мелкий опт";
  }

  if (isFishProduct(product)) {
    const weight = product.weightOfTheFish ?? 0;
    return {
      unitPrice: roundMoney(effectivePrice * weight),
      pricePerKg: roundMoney(effectivePrice),
      wholesaleLabel,
    };
  }

  return {
    unitPrice: roundMoney(effectivePrice),
    wholesaleLabel,
  };
}

export function getLineTotal(product: ProductUI, quantity: number): number {
  const { unitPrice } = resolveProductPrice(product, quantity);
  return roundMoney(unitPrice * quantity);
}

export function getQuantityUnit(product: Pick<ProductUI, "weightOfTheFish" | "msUnit">): string {
  // WEIGHT_OF_THE_FISH — средний вес штуки (рыба, дыня и т.п.): продаём штуками
  if (isFishProduct(product)) {
    return "шт";
  }
  const unit = product.msUnit?.trim();
  return unit || "шт";
}

export function formatUnitPriceLabel(
  product: ProductUI,
  quantity: number
): string {
  const { unitPrice, pricePerKg } = resolveProductPrice(product, quantity);
  const unit = getQuantityUnit(product);

  if (isFishProduct(product) && pricePerKg != null) {
    return `${pricePerKg.toLocaleString("ru-RU")} ₽/кг · ${unitPrice.toLocaleString("ru-RU")} ₽/${unit}`;
  }

  return `${unitPrice.toLocaleString("ru-RU")} ₽/${unit}`;
}

export function getOrderLineUnitPrice(
  product: ProductUI,
  quantity: number
): number {
  return resolveProductPrice(product, quantity).unitPrice;
}
